import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedRichText, LocalizedText } from "@/i18n/LocalizedText";
import { useEffect, useRef, useState } from "react";
import { auth, googleProvider, remoteConfig } from "../firebase";
import {
  signInWithEmailAndPassword,
  signInWithRedirect,
  signInWithPopup,
} from "firebase/auth";
import { Link, useLocation } from "react-router-dom";
import noboriLogo from "../assets/logo.svg";
import type { User } from "firebase/auth";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  Eye,
  EyeOff,
  MessageSquareQuoteIcon,
  Sparkles,
  Waypoints,
} from "lucide-react";
import { fetchAndActivate, getValue } from "firebase/remote-config";
import { useQuery } from "@tanstack/react-query";
import {
  detectInAppBrowser,
  getGoogleAuthErrorMessage,
  type InAppBrowserSource,
  resolveGoogleSignInMode,
} from "@/lib/googleAuth";
import { AUTH_FLOW_DEFAULTS, parseAuthFlowConfig } from "@/lib/authFlowConfig";
import { capturePosthogEvent } from "@/lib/posthog";
import InAppBrowserGoogleAuthDialog from "@/components/InAppBrowserGoogleAuthDialog";
import registrationIcon from "../assets/RegistrationIcon.svg";
import registrationBg from "../assets/registrationBg.svg";
import { renderProviderIcons } from "@/services/helpers";

const INVALID_LOGIN_MESSAGE = "Login or password is not correct";
const INVALID_LOGIN_ERROR_CODES = new Set([
  "auth/invalid-credential",
  "auth/invalid-login-credentials",
  "auth/user-not-found",
  "auth/wrong-password",
]);

const getEmailPasswordAuthErrorMessage = (err: unknown): string => {
  const code = String((err as { code?: string })?.code || "").toLowerCase();
  if (INVALID_LOGIN_ERROR_CODES.has(code)) {
    return INVALID_LOGIN_MESSAGE;
  }

  const raw = String(
    (err as { message?: string })?.message || "Failed to sign in",
  );
  const cleaned = raw
    .replace(/^Firebase:\s*/i, "")
    .replace(/\s*\(auth\/[^\)]+\)\.?/i, "")
    .trim();
  return cleaned && !/^error$/i.test(cleaned) ? cleaned : "Failed to sign in";
};

const AuthForm = () => {
  const { t } = useLocale();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showGoogleTryAgain, setShowGoogleTryAgain] = useState(false);
  const [inAppDialogOpen, setInAppDialogOpen] = useState(false);
  const [blockedSourceApp, setBlockedSourceApp] =
    useState<InAppBrowserSource>("unknown");
  const [inAppDetection] = useState(() =>
    detectInAppBrowser(
      typeof navigator !== "undefined" ? navigator.userAgent : "",
    ),
  );
  const inAppBlockTrackedRef = useRef(false);
  const errorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!error) return;

    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }

    errorTimeoutRef.current = setTimeout(() => {
      setError("");
      errorTimeoutRef.current = null;
    }, 3000);

    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = null;
      }
    };
  }, [error]);

  useEffect(() => {
    if (!inAppDetection.isInAppBrowser || inAppBlockTrackedRef.current) return;
    inAppBlockTrackedRef.current = true;
    setBlockedSourceApp(inAppDetection.sourceApp);
    capturePosthogEvent("in_app_browser_google_auth_blocked", {
      source_app: inAppDetection.sourceApp,
      page:
        typeof window !== "undefined" && window.location.pathname
          ? window.location.pathname
          : "unknown",
      flow: "login",
    });
  }, [inAppDetection.isInAppBrowser, inAppDetection.sourceApp]);

  const handleAuth = async (
    cb: () => Promise<{ user: User }>,
    opts?: { isGooglePopup?: boolean },
  ) => {
    setLoading(true);
    try {
      await cb();
    } catch (err) {
      const authCode = String(
        (err as { code?: string })?.code || "",
      ).toLowerCase();
      if (opts?.isGooglePopup) {
        if (
          authCode === "auth/popup-closed-by-user" ||
          authCode === "auth/cancelled-popup-request"
        ) {
          setShowGoogleTryAgain(true);
        }
      }
      if (opts?.isGooglePopup) {
        setError(getGoogleAuthErrorMessage(err));
      } else {
        setError(getEmailPasswordAuthErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const { data: authConfig, isLoading } = useQuery({
    queryKey: ["remoteConfig", "authFlow"],
    queryFn: async () => {
      try {
        await fetchAndActivate(remoteConfig);
        const rawValue = getValue(remoteConfig, "authFlow").asString();
        return parseAuthFlowConfig(rawValue);
      } catch {
        return { ...AUTH_FLOW_DEFAULTS };
      }
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    void handleAuth(() => signInWithEmailAndPassword(auth, email, password));
  };

  const handleGoogleSignIn = async () => {
    const userAgent =
      typeof navigator !== "undefined" ? navigator.userAgent : "";
    const inAppDetection = detectInAppBrowser(userAgent);

    if (inAppDetection.isInAppBrowser) {
      setBlockedSourceApp(inAppDetection.sourceApp);
      setInAppDialogOpen(true);
      setError("");
      return;
    }

    const mode = resolveGoogleSignInMode(userAgent);
    if (mode === "blocked") {
      setBlockedSourceApp("unknown");
      setInAppDialogOpen(true);
      setError("");
      capturePosthogEvent("in_app_browser_google_auth_blocked", {
        source_app: "unknown",
        page:
          typeof window !== "undefined" && window.location.pathname
            ? window.location.pathname
            : "unknown",
        flow: "login",
      });
      return;
    }

    if (mode === "redirect") {
      setLoading(true);
      setError("");
      try {
        await signInWithRedirect(auth, googleProvider);
      } catch (err) {
        setError(getGoogleAuthErrorMessage(err));
        setLoading(false);
      }
      return;
    }

    handleAuth(() => signInWithPopup(auth, googleProvider), {
      isGooglePopup: true,
    });
  };

  return (
    <div className="d-flex flex-column-fluid flex-row-auto justify-content-center mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 w-full">
        <div className="mx-auto w-full sm:w-auto mt-10 mb-10 bg-white px-6 sm:px-0">
          <div className="mx-6">
            <div className="flex justify-center mb-6">
              <img src={noboriLogo}></img>
            </div>

            <div className="w-full mx-auto sm:w-[466px] p-6 border bg-white rounded-lg">
              <h2 className="text-2xl font-semibold text-center mb-6">
                <LocalizedText id="legacy.sign.in.1hce6oh" />
              </h2>
              {inAppDetection.isInAppBrowser ? (
                <div className="mb-6 rounded-md border border-[#F4D2BF] bg-[#FFF7F2] px-4 py-3 text-sm text-[#7A3A1D]">
                  <p>
                    <LocalizedText id="legacy.google.sign.in.is.unavailable.in.this.in.t08833" />
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setBlockedSourceApp(inAppDetection.sourceApp);
                      setInAppDialogOpen(true);
                    }}
                    className="mt-2 cursor-pointer text-sm font-medium underline underline-offset-2"
                  >
                    <LocalizedText id="legacy.how.to.open.in.browser.1v1fegh" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleGoogleSignIn}
                  className="cursor-pointer mb-6 w-full border border-gray-300 text-gray-700 py-2 px-4 rounded-md flex items-center justify-center hover:bg-gray-100 transition"
                >
                  <img
                    src="https://www.svgrepo.com/show/475656/google-color.svg"
                    alt={t("legacy.google.16fzjn0")}
                    className="w-5 h-5 mr-2"
                  />
                  {showGoogleTryAgain ? "Try again" : "Continue with Google"}
                </button>
              )}

              {(authConfig?.isEmailPassLoginShow ?? true) && !isLoading && (
                <>
                  <div className="text-center mb-6 relative">
                    {" "}
                    <Separator />
                    <span className="capitalize font-sans text-[#737373] text-xs absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-2 bg-white">
                      <LocalizedText id="legacy.or.continue.with.hj9huj" />
                    </span>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label
                        className="mb-3 text-sm font-medium text-[#0A0A0A]"
                        htmlFor="email"
                      >
                        <LocalizedText id="legacy.email.3brsq1" />
                      </Label>
                      <Input
                        type="email"
                        id="email"
                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={t("legacy.enter.your.email.1qe62c0")}
                        value={email}
                        required
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>

                    <div className="relative">
                      <Label
                        className="mb-3 text-sm font-medium text-[#0A0A0A]"
                        htmlFor="password"
                      >
                        <LocalizedText id="legacy.password.1pd9l6i" />
                      </Label>
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        className="w-full pl-4 pr-10 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={t("legacy.enter.your.password.vkftk3")}
                        value={password}
                        required
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <button
                        className="absolute text-muted-foreground right-2 top-[40px] "
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                      >
                        {!showPassword ? (
                          <EyeOff size={20} strokeWidth={1.5} />
                        ) : (
                          <Eye size={20} strokeWidth={1.5} />
                        )}
                      </button>
                    </div>
                    <div className="text-right">
                      <Link
                        to="/forgot-password"
                        className="text-sm text-black underline underline-offset-2"
                      >
                        <LocalizedText id="legacy.forgot.password.1ibz3z6" />
                      </Link>
                    </div>

                    {error && (
                      <p className="text-red-500 text-sm text-center">
                        {error}
                      </p>
                    )}
                    <div className="flex items-center justify-center">
                      <Button
                        type="submit"
                        className="hover:bg-[#ff8138] text-sm cursor-pointer bg-[#FF5E00] text-white w-fit py-[15px] px-[50px] border rounded-md transition duration-200"
                      >
                        {loading ? (
                          <div className=" mr-2 flex justify-center items-center">
                            <Spinner
                              variant="circle-filled"
                              size={16}
                            ></Spinner>
                          </div>
                        ) : null}
                        <LocalizedText id="legacy.sign.in.1hce7g1" />
                      </Button>
                    </div>
                  </form>
                </>
              )}

              <div className="mt-4 text-center text-sm ">
                <LocalizedText id="legacy.dont.have.an.account.vu0wuw" />
                <Link
                  to={`/register/1${location.search}`}
                  replace
                  className="ml-1 text-black underline underline-offset-1"
                >
                  <LocalizedText id="legacy.sign.up.1hce6r7" />
                </Link>
              </div>
            </div>
            <div className="mt-6 mx-auto text-center text-xs text-muted-foreground max-w-[229px]">
              <LocalizedRichText
                id="auth.termsNotice"
                values={{
                  terms: (
                    <Link
                      to="https://nobori.ai/terms-of-service"
                      target="_blank"
                      className="ml-1 underline underline-offset-1"
                    >
                      <LocalizedText id="legacy.terms.of.service.1nx66rw" />
                    </Link>
                  ),
                  privacy: (
                    <Link
                      target="_blank"
                      className="ml-1 underline underline-offset-1"
                      to="https://nobori.ai/privacy-policy"
                    >
                      <LocalizedText id="legacy.privacy.policy.ot76bn" />
                    </Link>
                  ),
                }}
              />
            </div>
          </div>
        </div>
        <div
          style={{ backgroundImage: `url(${registrationBg})` }}
          className="p-9 bg-cover bg-center bg-no-repeat relative sticky flex justify-center top-0 hidden overflow-hidden lg:flex flex-grow bg-black h-screen"
        >
          <div className="w-full max-w-lg flex-col h-full flex items-start justify-start mt-[100px]">
            <img className="mb-8" src={registrationIcon}></img>
            <p className="text-white text-2xl mb-6 w-full ">
              <LocalizedRichText
                id="auth.hero.copy"
                values={{
                  brand: (
                    <span className="text-[#FF5E00]">
                      <LocalizedText id="legacy.your.brand.8dvymn" />
                    </span>
                  ),
                }}
              />
            </p>
            <div className="text-[#737373] w-full flex flex-col">
              <p className="text-lg mb-8">
                {" "}
                <LocalizedText id="legacy.we.show.what.ai.cites.and.what.to.114h2v0" />
              </p>
              <div className="text-base flex mb-6 gap-3 items-center">
                <div className="flex flwx-wrap">
                  <div className="flex w-fit items-center gap-3">
                    <Sparkles size={16}></Sparkles>
                    <LocalizedText id="legacy.track.your.visibility.and.ranking.in.ai.answers.lao9z3" />
                  </div>
                  <span className="capitalize ml-2 grayscale invert">
                    {renderProviderIcons(
                      ["GPT", "PERP", "GOOGLE", "AI-MODE"],
                      "size-6",
                    )}
                  </span>
                </div>
              </div>
              <p className="text-base flex mb-6 gap-3 items-center">
                <MessageSquareQuoteIcon size={16}></MessageSquareQuoteIcon>{" "}
                <LocalizedText id="legacy.see.cited.pages.and.domains.behind.the.shortlist.jk7l84" />
              </p>
              <p className="text-base flex mb-8 gap-3 items-center">
                <Waypoints size={16}></Waypoints>
                <LocalizedText id="legacy.get.tasks.and.a.strategy.for.each.buyer.mgav3w" />
              </p>
              <p className="text-xs">
                <LocalizedText id="legacy.youll.get.your.first.ai.visibility.strategy.right.ld7kjs" />
              </p>
            </div>
          </div>
        </div>
      </div>
      <InAppBrowserGoogleAuthDialog
        open={inAppDialogOpen}
        onOpenChange={setInAppDialogOpen}
        sourceApp={blockedSourceApp}
      />
    </div>
  );
};

export default AuthForm;
